/**
 * This file is temporarily placed here for development purposes.
 * It will be moved into a shared package to be used across other projects.
 */

import { create } from 'zustand';
import { signIn, confirmSignIn, type SignInOutput, fetchAuthSession, getCurrentUser, signOut } from 'aws-amplify/auth';
import { Amplify } from 'aws-amplify';
import { jwtDecode } from 'jwt-decode';

export type User = {
  email?: string;
  sub?: string;
  name?: string;
  exp?: number;
  [key: string]: unknown;
};

export type FederatedAuthOptions = {
  enableAutoLogin?: boolean;
  enableAutoTokenRefetch?: boolean;
  cognitoDomain: string;
  userPoolId: string;
  userPoolClientId: string;
  redirectUri: string;
  identityProvider: string;
  customScope?: string;
  tenantId: string;
};

export type LoginStatus = 'loggedOut' | 'waitingForAuthCode' | 'loggingIn' | 'loggedIn' | 'otpRequested';

export type OtpRequestInfo = {
  username: string;
  otpResponse: SignInOutput;
  expiry: Date;
  receivedAt: Date;
};

type AuthState = {
  accessToken: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  error: string | null;
  config: FederatedAuthOptions | null;
  loginStatus: LoginStatus;
  otpRequestInfo: OtpRequestInfo | null;
  hasInitialized: boolean;
  pendingAuthCode: string | null;

  initialize: (config: FederatedAuthOptions) => Promise<void>;
  login: () => void;
  logout: () => void;
  submitAuthCode: (code: string) => Promise<void>;
  exchangeCodeForToken: (code: string) => Promise<void>;
  refreshAccessToken: () => Promise<void>;
  requestOTP: (username: string) => Promise<SignInOutput>;
  signInWithOTP: (code: string) => Promise<SignInOutput>;
  resetOtpRequestInfo: () => void;
};

export const useAuthStore = create<AuthState>((set, get) => {
  let refreshTimeout: ReturnType<typeof setTimeout> | null = null;

  const scheduleTokenRefresh = () => {
    const { accessToken, config, refreshToken } = get();
    if (accessToken && config?.enableAutoTokenRefetch && refreshToken) {
      const decoded = jwtDecode<User>(accessToken);
      if (decoded.exp) {
        const nowInSeconds = Date.now() / 1000;
        const expiresIn = decoded.exp - nowInSeconds;
        const refreshDelay = (expiresIn - 60) * 1000;
        if (refreshTimeout) {
          clearTimeout(refreshTimeout);
        }
        if (refreshDelay > 0) {
          refreshTimeout = setTimeout(() => {
            get().refreshAccessToken();
          }, refreshDelay);
        } else {
          get().refreshAccessToken();
        }
      }
    }
  };

  return {
    user: null,
    accessToken: null,
    refreshToken: null,
    isLoading: false,
    error: null,
    config: null,
    loginStatus: 'loggedOut',
    otpRequestInfo: null,
    hasInitialized: false,
    pendingAuthCode: null,

    initialize: async (config: FederatedAuthOptions) => {
      let enableAutoLogin = config.enableAutoLogin;
      if (
        get().hasInitialized ||
        (sessionStorage.getItem(`${config.tenantId}_authStore_Initialized`) === 'true' && !config.enableAutoLogin) ||
        localStorage.getItem(`${config.tenantId}_authStore_DisableAutoLogin`) === 'true'
      ) {
        enableAutoLogin = false;
        console.log('Auto-login is disabled.');
      }

      sessionStorage.setItem(`${config.tenantId}_authStore_Initialized`, 'true');

      Amplify.configure({
        Auth: {
          Cognito: {
            userPoolId: config.userPoolId,
            userPoolClientId: config.userPoolClientId,
          },
        },
      });

      set({ config, isLoading: true });

      const currentUser = await getCurrentUser().catch(() => null);
      let isUsingSameSession = false;

      if (currentUser) {
        const session = await fetchAuthSession({ forceRefresh: true });
        const sessionTenantCode = session.tokens?.accessToken?.payload?.['custom:tenantCode'];
        isUsingSameSession = sessionTenantCode === config.tenantId;

        if (isUsingSameSession) {
          set({
            accessToken: session.tokens?.accessToken.toString(),
            refreshToken: null,
            loginStatus: 'loggedIn',
          });

          if (config.enableAutoTokenRefetch) {
            scheduleTokenRefresh();
          }
        } else {
          console.warn('Warning: The current session does not match the expected tenant.');
        }
      }

      if (enableAutoLogin && (!currentUser || !isUsingSameSession)) {
        console.log('Auto-login enabled, triggering login...');
        set({ loginStatus: 'waitingForAuthCode' });
        set({ hasInitialized: true });
        get().login();
      }

      scheduleTokenRefresh();
      set({ isLoading: false, hasInitialized: true });
    },

    login: () => {
      const { config } = get();
      if (!config) {
        console.error('No config provided for login.');
        return;
      }
      set({ loginStatus: 'loggingIn' });
      const authUrl = `${config.cognitoDomain}/oauth2/authorize?client_id=${config.userPoolClientId}&response_type=code&scope=openid+email+profile+${config.customScope || ''
        }&redirect_uri=${config.redirectUri}&identity_provider=${config.identityProvider}`;
      console.log('Redirecting to auth URL:', authUrl);
      window.location.href = authUrl;
    },

    submitAuthCode: async (code: string) => {
      const { config } = get();
      set({ pendingAuthCode: code, isLoading: true });

      try {
        console.log('Manually submitted auth code, exchanging code for tokens...');
        await get().exchangeCodeForToken(code);
        const storedAccessToken = sessionStorage.getItem(`${config?.tenantId}_authStore_AccessToken`);
        if (storedAccessToken) {

          localStorage.removeItem(`${config?.tenantId}_authStore_DisableAutoLogin`);
          set({ loginStatus: 'loggedIn' });
        } else {

          set({ loginStatus: 'loggedOut' });
          localStorage.setItem(`${config?.tenantId}_authStore_DisableAutoLogin`, 'true');
        }
      } catch (_error) {

        set({ error: 'Error submitting auth code' });
        localStorage.setItem(`${config?.tenantId}_authStore_DisableAutoLogin`, 'true');
      } finally {
        set({ isLoading: false });
      }
    },

    exchangeCodeForToken: async (code: string) => {
      const { config } = get();
      if (!config) {
        console.error('No config provided for token exchange');
        return;
      }

      set({ isLoading: true });
      try {
        const response = await fetch(`${config.cognitoDomain}/oauth2/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            grant_type: 'authorization_code',
            client_id: config.userPoolClientId,
            code,
            redirect_uri: config.redirectUri,
          }),
        });
        const data = await response.json();
        console.log('Token exchange response:', data);

        if (data.access_token) {
          sessionStorage.setItem(`${config.tenantId}_authStore_AccessToken`, data.access_token);
          set({ accessToken: data.access_token });
        }
        if (data.refresh_token) {
          sessionStorage.setItem(`${config.tenantId}_authStore_RefreshToken`, data.refresh_token);
          set({ refreshToken: data.refresh_token });
        }
        set({ pendingAuthCode: null });
        window.history.replaceState(null, '', window.location.pathname);
        scheduleTokenRefresh();
      } catch (error) {
        console.error('Error exchanging auth code:', error);
        set({ error: 'Error exchanging auth code' });
        localStorage.setItem(`${config?.tenantId}_authStore_DisableAutoLogin`, 'true');
      } finally {
        set({ isLoading: false });
      }
    },

    refreshAccessToken: async () => {
      const { config, refreshToken } = get();
      if (!config || !refreshToken) {
        return;
      }
      try {
        const response = await fetch(`${config.cognitoDomain}/oauth2/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            grant_type: 'authStore_RefreshToken',
            client_id: config.userPoolClientId,
            refresh_token: refreshToken,
          }),
        });
        const data = await response.json();
        if (data.access_token) {
          sessionStorage.setItem(`${config.tenantId}_authStore_AccessToken`, data.access_token);
          set({ accessToken: data.access_token });
          scheduleTokenRefresh();
        }
      } catch (error) {
        console.error('Error refreshing access token:', error);
        set({ error: 'Error refreshing access token' });
      }
    },

    logout: () => {
      const { config } = get();
      sessionStorage.removeItem(`${config?.tenantId}_authStore_AccessToken`);
      sessionStorage.removeItem(`${config?.tenantId}_authStore_RefreshToken`);
      sessionStorage.removeItem(`${config?.tenantId}_authStore_LoginAttempted`);
      sessionStorage.removeItem(`${config?.tenantId}_authStore_Initialized`);
      localStorage.setItem(`${config?.tenantId}_authStore_DisableAutoLogin`, 'true');

      set({
        accessToken: null,
        refreshToken: null,
        loginStatus: 'loggedOut',
        otpRequestInfo: null,
        hasInitialized: false,
        pendingAuthCode: null,
      });

      signOut();

      if (config) {
        window.location.href = `${config.cognitoDomain}/logout?client_id=${config.userPoolClientId}&logout_uri=${config.redirectUri}`;
      }
    },

    requestOTP: async (username: string) => {
      set({ isLoading: true });

      try {
        const currentUser = await getCurrentUser().catch(() => null);
        if (currentUser) {
          await signOut();
        }

        const result = await signIn({
          username,
          options: { authFlowType: 'CUSTOM_WITHOUT_SRP' },
        });

        const receivedAt = new Date();
        const expiry = new Date(receivedAt.getTime() + 5 * 60 * 1000);

        set({
          loginStatus: 'otpRequested',
          otpRequestInfo: {
            username,
            otpResponse: result,
            expiry,
            receivedAt,
          },
        });
        return result;
      } catch (error) {
        console.error('Error requesting OTP:', error);
        throw error;
      } finally {
        set({ isLoading: false });
      }
    },

    signInWithOTP: async (code: string) => {
      set({ isLoading: true });
      const { config } = get();

      try {
        const result = await confirmSignIn({ challengeResponse: code });
        set({ loginStatus: 'loggedIn', otpRequestInfo: null });
        localStorage.removeItem(`${config?.tenantId}_authStore_DisableAutoLogin`);
        await get().refreshAccessToken();

        const session = await fetchAuthSession({ forceRefresh: true });
        const sessionTenantCode = session.tokens?.accessToken?.payload?.['custom:tenantCode'];
        if (sessionTenantCode !== config?.tenantId) {
          throw new Error('Session error: Invalid tenant association detected.');
        }

        set({
          accessToken: session.tokens?.accessToken.toString(),
          refreshToken: null,
        });
        return result;
      } catch (error) {
        console.error('Error signing in with OTP:', error);
        throw error;
      } finally {
        set({ isLoading: false });
      }
    },

    resetOtpRequestInfo: () => {
      set({ otpRequestInfo: null });
    },
  };
});
